import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFilterStore } from '@/stores/filterStore';
import { useSearchStore } from '@/stores/searchStore';
import { useNearMeStore, RADIUS_OPTIONS } from '@/stores/nearMeStore';
import { api } from '@/lib/api';
import { useT, t as tStatic } from '@/lib/i18n';
import { T } from '@/components/T';

/* ═══════════════════════════════════════════════════════════════
   Page configuration — defines What / Where / When per page
   ═══════════════════════════════════════════════════════════════ */

export type PageKey = '/map' | '/discover' | '/routes' | '/profiles' | '/artradar' | '/events';

interface PageFilterConfig {
  what: WhatFilter[];
  where: WhereFilter[];
  when: WhenFilter[];
}

type WhatFilter = 'pinType' | 'routeType' | 'profileType' | 'articleCategory' | 'eventType' | 'discoverType';
type WhereFilter = 'city' | 'country' | 'nearMe';
type WhenFilter = 'dateRange' | 'eventSort';

export const PAGE_FILTER_CONFIG: Record<PageKey, PageFilterConfig> = {
  '/map':      { what: ['pinType'],          where: ['city', 'country', 'nearMe'], when: [] },
  '/discover': { what: ['discoverType'],     where: ['city', 'country', 'nearMe'], when: [] },
  '/routes':   { what: ['routeType'],        where: ['country', 'nearMe'],         when: [] },
  '/profiles': { what: ['profileType'],      where: ['city', 'country', 'nearMe'], when: [] },
  '/artradar': { what: ['articleCategory'],  where: ['nearMe'],                    when: [] },
  '/events':   { what: ['eventType'],        where: ['city', 'country', 'nearMe'], when: ['dateRange', 'eventSort'] },
};

/** Content search placeholder per page */
const CONTENT_PLACEHOLDERS: Record<string, string> = {
  '/map': 'Search galleries, museums, studios...',
  '/discover': 'Search artists, events, articles...',
  '/routes': 'Search routes by name or description...',
  '/profiles': 'Search artists, collectives, curators...',
  '/artradar': 'Search articles, reviews, guides...',
  '/events': 'Search events, venues, festivals...',
};

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */

const PIN_TYPES = [
  { key: 'gallery',       labelKey: 'pinType.gallery',       color: '#F5A623', icon: 'fa-solid fa-building-columns' },
  { key: 'museum',        labelKey: 'pinType.museum',        color: '#3B5998', icon: 'fa-solid fa-landmark' },
  { key: 'art_centre',    labelKey: 'pinType.art_centre',    color: '#B83A4B', icon: 'fa-solid fa-palette' },
  { key: 'studio',        labelKey: 'pinType.studio',        color: '#34c759', icon: 'fa-solid fa-paintbrush' },
  { key: 'venue',         labelKey: 'pinType.venue',         color: '#9b59b6', icon: 'fa-solid fa-location-dot' },
  { key: 'artist',        labelKey: 'pinType.artist',        color: '#FF6B6B', icon: 'fa-solid fa-user' },
  { key: 'heritage',      labelKey: 'pinType.heritage',      color: '#8B6914', icon: 'fa-solid fa-monument' },
  { key: 'supplier',      labelKey: 'pinType.supplier',      color: '#607D8B', icon: 'fa-solid fa-truck' },
  { key: 'education',     labelKey: 'pinType.education',     color: '#2196F3', icon: 'fa-solid fa-graduation-cap' },
  { key: 'residency',     labelKey: 'pinType.residency',     color: '#FF7043', icon: 'fa-solid fa-house-chimney' },
  { key: 'public_art',    labelKey: 'pinType.public_art',    color: '#E91E63', icon: 'fa-solid fa-tree-city' },
  { key: 'cultural_site', labelKey: 'pinType.cultural_site', color: '#795548', icon: 'fa-solid fa-archway' },
  { key: 'collection',    labelKey: 'pinType.collection',    color: '#00897B', icon: 'fa-solid fa-layer-group' },
];

const ROUTE_TYPES = [
  { value: 'art_trail', icon: 'fas fa-palette',       labelKey: 'routeType.art_trail' },
  { value: 'heritage',  icon: 'fas fa-landmark',      labelKey: 'routeType.heritage' },
  { value: 'walking',   icon: 'fas fa-hiking',        labelKey: 'routeType.walking' },
  { value: 'cycling',   icon: 'fas fa-bicycle',       labelKey: 'routeType.cycling' },
  { value: 'cultural',  icon: 'fas fa-masks-theater',  labelKey: 'routeType.cultural' },
];

const PROFILE_TYPES = [
  { value: 'artist',     icon: 'fas fa-user',    labelKey: 'profile.artist' },
  { value: 'collective', icon: 'fas fa-users',   labelKey: 'profile.collective' },
  { value: 'curator',    icon: 'fas fa-glasses',  labelKey: 'profile.curator' },
];

const ARTICLE_CATEGORIES = [
  { value: 'gallery_spotlight',  icon: 'fas fa-star',        labelKey: 'article.category.gallery_spotlight' },
  { value: 'artist_profile',    icon: 'fas fa-user',        labelKey: 'article.category.artist_profile' },
  { value: 'exhibition_review', icon: 'fas fa-eye',         labelKey: 'article.category.exhibition_review' },
  { value: 'cultural_insight',  icon: 'fas fa-lightbulb',   labelKey: 'article.category.cultural_insight' },
  { value: 'route_guide',       icon: 'fas fa-route',       labelKey: 'article.category.route_guide' },
  { value: 'news',              icon: 'fas fa-newspaper',   labelKey: 'article.category.news' },
  { value: 'event',             icon: 'fas fa-calendar',    labelKey: 'article.category.event' },
  { value: 'opinion',           icon: 'fas fa-comment',     labelKey: 'article.category.opinion' },
];

const EVENT_TYPES = [
  { value: 'exhibition',   icon: 'fas fa-image',          labelKey: 'event.exhibition' },
  { value: 'opening',      icon: 'fas fa-door-open',      labelKey: 'event.opening' },
  { value: 'fair',         icon: 'fas fa-store',          labelKey: 'event.art_fair' },
  { value: 'workshop',     icon: 'fas fa-tools',          labelKey: 'event.workshop' },
  { value: 'open_studio',  icon: 'fas fa-paint-brush',    labelKey: 'event.open_studio' },
  { value: 'performance',  icon: 'fas fa-theater-masks',  labelKey: 'event.performance' },
  { value: 'festival',     icon: 'fas fa-music',          labelKey: 'event.festival' },
  { value: 'talk',         icon: 'fas fa-microphone',     labelKey: 'event.talk' },
  { value: 'tour',         icon: 'fas fa-walking',        labelKey: 'event.guided_tour' },
];

const DISCOVER_TYPES = [
  { value: 'pin',       icon: 'fas fa-map-marker-alt', labelKey: 'discover.places' },
  { value: 'heritage',  icon: 'fas fa-landmark',       labelKey: 'discover.heritage' },
  { value: 'event',     icon: 'fas fa-calendar-alt',   labelKey: 'discover.events' },
  { value: 'article',   icon: 'fas fa-newspaper',      labelKey: 'discover.articles' },
  { value: 'route',     icon: 'fas fa-route',          labelKey: 'discover.routes' },
  { value: 'profile',   icon: 'fas fa-palette',        labelKey: 'discover.artists' },
  { value: 'artwork',   icon: 'fas fa-image',          labelKey: 'discover.artworks' },
];

const SORT_OPTIONS = [
  { value: 'start_date_desc',  icon: 'fas fa-arrow-down', labelKey: 'eventsList.latestFirst' },
  { value: 'start_date_asc',   icon: 'fas fa-arrow-up',   labelKey: 'eventsList.soonestFirst' },
  { value: 'closest_to_now',   icon: 'fas fa-clock',      labelKey: 'eventsList.happeningNow' },
];

const ACCORDION_STORAGE_KEY = 'artrouting-filter-accordion-open';

function loadAccordionState(): boolean {
  try { return localStorage.getItem(ACCORDION_STORAGE_KEY) === 'true'; }
  catch { return false; }
}

/* ═══════════════════════════════════════════════════════════════
   Label lookups for toggle-bar chips
   ═══════════════════════════════════════════════════════════════ */

const FILTER_LABELS: Record<string, Record<string, string>> = {
  routeType: { art_trail: 'Art Trail', heritage: 'Heritage', walking: 'Walking', cycling: 'Cycling', cultural: 'Cultural' },
  profileType: { artist: 'Artist', collective: 'Collective', curator: 'Curator' },
  eventType: {
    exhibition: 'Exhibition', opening: 'Opening', fair: 'Fair', workshop: 'Workshop',
    open_studio: 'Open Studio', performance: 'Performance', festival: 'Festival', talk: 'Talk', tour: 'Tour',
  },
  discoverType: { pin: 'Places', heritage: 'Heritage', event: 'Events', article: 'Articles', route: 'Routes', profile: 'Artists', artwork: 'Artworks' },
};

const CATEGORY_LABELS: Record<string, string> = {
  gallery_spotlight: 'Gallery Spotlight', artist_profile: 'Artist Profile',
  exhibition_review: 'Exhibition Review', cultural_insight: 'Cultural Insight',
  route_guide: 'Route Guide', news: 'News', event: 'Event', opinion: 'Opinion',
};

interface SearchLabel { icon: string; label: string; onClear: () => void }

/** Build labels for active search terms shown in the toggle bar */
function getActiveSearchLabels(
  search: { query: string; contentQuery: string; country: string; setQuery: (v: string) => void; setContentQuery: (v: string) => void; setCountry: (v: string) => void },
  filters: {
    routeType: string; profileType: string; articleCategory: string;
    eventType: string; discoverType: string;
    pinTypes: Record<string, boolean>;
    setRouteType: (v: string) => void; setProfileType: (v: string) => void;
    setArticleCategory: (v: string) => void; setEventType: (v: string) => void;
    setDiscoverType: (v: string) => void; setPinTypesAll: (v: boolean) => void;
  },
  nearMe: { active: boolean; radius: number; turnOff: () => void; setCityName: (v: string) => void },
): SearchLabel[] {
  const labels: SearchLabel[] = [];
  if (nearMe.active) {
    const r = RADIUS_OPTIONS.find((o) => o.value === nearMe.radius);
    labels.push({ icon: 'fas fa-location-crosshairs', label: r?.label || `${nearMe.radius} km`, onClear: () => nearMe.turnOff() });
  }
  if (search.query) labels.push({ icon: 'fas fa-city', label: search.query, onClear: () => { search.setQuery(''); } });
  if (search.contentQuery) labels.push({ icon: 'fas fa-search', label: search.contentQuery, onClear: () => search.setContentQuery('') });
  if (search.country) labels.push({ icon: 'fas fa-flag', label: search.country, onClear: () => search.setCountry('') });
  if (filters.routeType) labels.push({ icon: 'fas fa-route', label: FILTER_LABELS.routeType[filters.routeType] || filters.routeType, onClear: () => filters.setRouteType('') });
  if (filters.profileType) labels.push({ icon: 'fas fa-user', label: FILTER_LABELS.profileType[filters.profileType] || filters.profileType, onClear: () => filters.setProfileType('') });
  if (filters.articleCategory) labels.push({ icon: 'fas fa-newspaper', label: CATEGORY_LABELS[filters.articleCategory] || filters.articleCategory, onClear: () => filters.setArticleCategory('') });
  if (filters.eventType) labels.push({ icon: 'fas fa-calendar-alt', label: FILTER_LABELS.eventType[filters.eventType] || filters.eventType, onClear: () => filters.setEventType('') });
  if (filters.discoverType && filters.discoverType !== 'all') labels.push({ icon: 'fas fa-compass', label: FILTER_LABELS.discoverType[filters.discoverType] || filters.discoverType, onClear: () => filters.setDiscoverType('') });
  const pinTypesOff = Object.values(filters.pinTypes).filter((v) => !v).length;
  if (pinTypesOff > 0) labels.push({ icon: 'fas fa-map-marker-alt', label: tStatic('search.hiddenCount').replace('{n}', String(pinTypesOff)), onClear: () => filters.setPinTypesAll(true) });
  return labels;
}

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */

interface FilterAccordionProps {
  page: PageKey;
}

export default function FilterAccordion({ page }: FilterAccordionProps) {
  const t = useT();
  const { pathname } = useLocation();
  const filters = useFilterStore();
  const search = useSearchStore();
  const nearMe = useNearMeStore();
  const [open, setOpen] = useState(loadAccordionState);
  const [countries, setCountries] = useState<string[]>([]);
  const activeSearchLabels = getActiveSearchLabels(search, filters, nearMe);
  const config = PAGE_FILTER_CONFIG[page];

  // Search input refs + local state
  const cityInputRef = useRef<HTMLInputElement>(null);
  const contentInputRef = useRef<HTMLInputElement>(null);
  const cityDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const contentDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [cityValue, setCityValue] = useState(search.query);
  const [contentValue, setContentValue] = useState(search.contentQuery);

  // Sync local inputs when store changes externally
  useEffect(() => { setCityValue(search.query); }, [search.query]);
  useEffect(() => { setContentValue(search.contentQuery); }, [search.contentQuery]);

  // Load user's configured Near Me town from profile (if logged in)
  useEffect(() => {
    useNearMeStore.getState().loadUserTown();
  }, []);

  // Restore persisted Near Me city name on mount (display only — no search query)
  useEffect(() => {
    if (nearMe.active && nearMe.cityName && !search.query) {
      setCityValue(nearMe.cityName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // Load countries — use cached version if available, fetch once only
  useEffect(() => {
    const CACHE_KEY = 'artrouting-countries-cache';
    const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { countries: c, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL && Array.isArray(c) && c.length > 0) {
          setCountries(c);
          return; // use cache, skip API
        }
      }
    } catch { /* ignore */ }
    // Fetch from API and cache
    api.get<{ allCountries?: string[] }>('/search?q=a&limit=1')
      .then((data) => {
        if (data.allCountries && data.allCountries.length > 0) {
          setCountries(data.allCountries);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ countries: data.allCountries, ts: Date.now() })); } catch { /* */ }
        }
      })
      .catch(() => {});
  }, []);

  const toggleAccordion = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(ACCORDION_STORAGE_KEY, String(next)); } catch { /* */ }
      return next;
    });
  }, []);

  const hasWhat = config.what.length > 0;
  const hasWhere = config.where.length > 0;
  const hasWhen = config.when.length > 0;

  // ── Search handlers ──
  const handleNearbyToggle = useCallback(() => {
    const wasActive = nearMe.active;
    nearMe.toggle();
    if (!wasActive) {
      // Activating Near Me — clear text search, Near Me uses radius not text matching
      search.setQuery('');
      search.setCountry('');
      setTimeout(() => {
        const state = useNearMeStore.getState();
        if (!state.active) return;

        // If user has a configured Near Me town, display it but don't set as search query
        if (state.userTown && state.cityName === state.userTown) {
          setCityValue(state.cityName);
          return;
        }

        if (state.lat === null || state.lng === null) return;

        // If we already have a persisted city name for these coordinates, display it
        if (state.cityName) {
          setCityValue(state.cityName);
          return;
        }

        // No persisted city — reverse-geocode from GPS (zoom=16 for street-level precision)
        setCityValue('');
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${state.lat}&lon=${state.lng}&zoom=16&addressdetails=1`, {
          headers: { 'Accept-Language': 'en' },
        })
          .then((r) => r.json())
          .then((data: { address?: { city?: string; town?: string; village?: string; suburb?: string } }) => {
            const addr = data.address;
            if (addr) {
              const city = addr.city || addr.town || addr.village || addr.suburb || '';
              if (city) {
                setCityValue(city);
                useNearMeStore.getState().setCityName(city);
              }
            }
          })
          .catch(() => {});
      }, 800); // Slightly longer timeout to allow forward-geocode to complete
    }
  }, [nearMe, search]);

  const handleCityInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setCityValue(val);
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      const trimmed = val.trim();
      search.setQuery(trimmed);
      // Near Me cityName is set ONLY by GPS reverse geocoding — never overwritten by manual input.
      // This ensures toggling Near Me always restores the GPS-derived city.
    }, 250);
  }, [search, nearMe]);

  const handleCityClear = useCallback(() => {
    search.setQuery('');
    setCityValue('');
    // Don't clear nearMe.cityName — GPS city is preserved so Near Me toggle restores it
    cityInputRef.current?.focus();
  }, [search]);

  const handleContentInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setContentValue(val);
    if (contentDebounceRef.current) clearTimeout(contentDebounceRef.current);
    contentDebounceRef.current = setTimeout(() => {
      search.setContentQuery(val.trim());
    }, 250);
  }, [search]);

  const handleContentClear = useCallback(() => {
    search.setContentQuery('');
    setContentValue('');
    contentInputRef.current?.focus();
  }, [search]);

  const handleKeyDown = useCallback((clearFn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') clearFn();
  }, []);

  const contentPlaceholder = CONTENT_PLACEHOLDERS[pathname] || 'Search...';

  return (
    <div className="filter-accordion" translate="no">

      {/* ════ TOGGLE BAR — always visible ════ */}
      <div className="filter-accordion-toggle" onClick={toggleAccordion} role="button" tabIndex={0}>
        <i className="fas fa-search" />
        <span>{t('common.search', 'Search')}{'...'}</span>
        {activeSearchLabels.map((chip, i) => (
          <span key={i} className="filter-accordion-badge">
            <i className={chip.icon} style={{ fontSize: 9, marginRight: 3 }} />
            {chip.label}
            <span className="filter-accordion-badge-x" onClick={(e) => { e.stopPropagation(); chip.onClear(); }}>&times;</span>
          </span>
        ))}
        {activeSearchLabels.length > 0 && (
          <button
            className="filter-accordion-clear-all"
            onClick={(e) => { e.stopPropagation(); nearMe.turnOff(); search.clearAll(); filters.clearAllFilters(); }}
          >
            {t('common.clearAll', 'Clear all')}
          </button>
        )}
        <i className={'fas fa-chevron-down filter-accordion-chevron' + (open ? ' open' : '')} />
      </div>

      {/* ════ COLLAPSIBLE BODY — search bars + filters ════ */}
      <div className={'filter-accordion-body' + (open ? '' : ' collapsed')}>

        {/* ── Search bars ── */}
        <div className="filter-search-bar">
          {/* Near Me */}
          <div className="search-toolbar-group search-toolbar-nearme-group">
            <button
              className={'search-toolbar-nearme-btn' + (nearMe.active ? ' active' : '') + (nearMe.locating ? ' locating' : '')}
              onClick={handleNearbyToggle}
            >
              <i className="fas fa-location-crosshairs" />
              <span>{nearMe.locating ? t('common.locating', 'Locating...') : nearMe.active ? t('common.nearMeOn', 'Near Me') : t('common.nearMe', 'Near Me')}</span>
            </button>
            <select
              className={'search-toolbar-radius' + (nearMe.active ? ' active' : '')}
              value={nearMe.radius}
              title={t('search.searchRadius')}
              onChange={(e) => nearMe.setRadius(parseFloat(e.target.value))}
            >
              {RADIUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Radius Ring toggle (map page only) */}
          {pathname === '/map' && (
            <button
              className={'search-toolbar-nearme-btn' + (filters.showRadiusRing ? ' active' : '')}
              onClick={() => filters.setShowRadiusRing(!filters.showRadiusRing)}
              title={filters.showRadiusRing ? t('search.hideRadiusRing') : t('search.showRadiusRing')}
              style={{ minWidth: 'auto', padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
            >
              <i className="fas fa-circle-notch" />
              <span><T k="search.radiusLabel" /></span>
            </button>
          )}

          {/* City / Town search */}
          <div className="search-toolbar-group search-toolbar-city-group" style={{ position: 'relative' }}>
            <div className="search-toolbar-search">
              <i className="fas fa-map-marker-alt search-toolbar-search-icon" />
              <input
                ref={cityInputRef}
                type="text"
                placeholder={t('common.searchPlaceholder', 'Search city or town...')}
                autoComplete="off"
                value={cityValue}
                onChange={handleCityInput}
                onKeyDown={handleKeyDown(handleCityClear)}
              />
              {cityValue && (
                <button className="search-toolbar-clear" title={t('search.clear')} onClick={handleCityClear}>
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
            {/* Location disambiguation dropdown */}
            {search.geoChoices.length > 1 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                background: 'var(--bg-primary, #1a1a2e)', borderRadius: '0 0 0.5rem 0.5rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.5)', padding: '0.25rem 0',
                border: '1px solid var(--accent, #F5A623)', borderTop: 'none',
                maxHeight: 200, overflowY: 'auto',
              }}>
                <div style={{ padding: '0.3rem 0.6rem', fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  Multiple locations — select one:
                </div>
                {search.geoChoices.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => search.selectGeoChoice(c)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '0.4rem 0.6rem',
                      background: 'none', border: 'none', color: 'var(--text-primary, #e0e0e0)',
                      cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1.3,
                    }}
                    onMouseOver={(e) => { (e.target as HTMLElement).style.background = 'var(--bg-secondary, #2a2a3e)'; }}
                    onMouseOut={(e) => { (e.target as HTMLElement).style.background = 'none'; }}
                  >
                    <i className="fas fa-map-marker-alt" style={{ marginRight: 6, color: '#3B5998' }} />
                    {c.display}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content search */}
          <div className="search-toolbar-group search-toolbar-content-group">
            <div className="search-toolbar-search">
              <i className="fas fa-search search-toolbar-search-icon" />
              <input
                ref={contentInputRef}
                type="text"
                placeholder={contentPlaceholder}
                autoComplete="off"
                value={contentValue}
                onChange={handleContentInput}
                onKeyDown={handleKeyDown(handleContentClear)}
              />
              {contentValue && (
                <button className="search-toolbar-clear" title={t('search.clear')} onClick={handleContentClear}>
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Pin Types */}
        {hasWhat && config.what.includes('pinType') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-group-label">{t('common.pinType', 'Pin Type')}</div>
              <div className="filter-pin-grid">
                {PIN_TYPES.map((pt) => (
                  <div
                    key={pt.key}
                    className={'filter-pin-item' + (filters.pinTypes[pt.key] ? ' active' : '')}
                    onClick={() => filters.setPinType(pt.key, !filters.pinTypes[pt.key])}
                  >
                    <span className="filter-pin-dot" style={{ background: pt.color }} />
                    <span className={'filter-pin-check' + (filters.pinTypes[pt.key] ? ' checked' : '')}>
                      {filters.pinTypes[pt.key] && '\u2713'}
                    </span>
                    <span>{t(pt.labelKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Route Types */}
        {hasWhat && config.what.includes('routeType') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-pills">
                {ROUTE_TYPES.map((rt) => (
                  <button key={rt.value} className={'filter-pill' + (filters.routeType === rt.value ? ' active' : '')}
                    onClick={() => filters.setRouteType(filters.routeType === rt.value ? '' : rt.value)}>
                    <i className={rt.icon} /> {t(rt.labelKey, rt.value)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Discover Types */}
        {hasWhat && config.what.includes('discoverType') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-pills">
                {DISCOVER_TYPES.map((dt) => (
                  <button key={dt.value} className={'filter-pill' + (filters.discoverType === dt.value ? ' active' : '')}
                    onClick={() => filters.setDiscoverType(filters.discoverType === dt.value ? '' : dt.value)}>
                    <i className={dt.icon} /> {t(dt.labelKey, dt.value)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Profile Types */}
        {hasWhat && config.what.includes('profileType') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-pills">
                {PROFILE_TYPES.map((pt) => (
                  <button key={pt.value} className={'filter-pill' + (filters.profileType === pt.value ? ' active' : '')}
                    onClick={() => filters.setProfileType(filters.profileType === pt.value ? '' : pt.value)}>
                    <i className={pt.icon} /> {t(pt.labelKey, pt.value)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Article Categories */}
        {hasWhat && config.what.includes('articleCategory') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-pills">
                {ARTICLE_CATEGORIES.map((ac) => (
                  <button key={ac.value} className={'filter-pill' + (filters.articleCategory === ac.value ? ' active' : '')}
                    onClick={() => filters.setArticleCategory(filters.articleCategory === ac.value ? '' : ac.value)}>
                    <i className={ac.icon} /> {t(ac.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Event Types */}
        {hasWhat && config.what.includes('eventType') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-pills">
                {EVENT_TYPES.map((et) => (
                  <button key={et.value} className={'filter-pill' + (filters.eventType === et.value ? ' active' : '')}
                    onClick={() => filters.setEventType(filters.eventType === et.value ? '' : et.value)}>
                    <i className={et.icon} /> {t(et.labelKey, et.value)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Event time filter — map + discover pages */}
        {(page === '/map' || page === '/discover') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-group-label">{t('map.eventFilter.label', 'Events')}</div>
              <div className="filter-pills">
                {([
                  { key: 'all' as const,   icon: 'fas fa-calendar', labelKey: 'map.eventFilter.all' },
                  { key: 'now' as const,   icon: 'fas fa-circle',   labelKey: 'map.eventFilter.now' },
                  { key: 'week' as const,  icon: 'fas fa-calendar-week', labelKey: 'map.eventFilter.week' },
                  { key: 'month' as const, icon: 'fas fa-calendar-alt',  labelKey: 'map.eventFilter.month' },
                  { key: 'later' as const, icon: 'fas fa-forward',        labelKey: 'map.eventFilter.later' },
                ]).map(({ key, icon, labelKey }) => (
                  <button
                    key={key}
                    className={'filter-pill' + (filters.eventTimeFilter === key ? ' active' : '')}
                    onClick={() => filters.setEventTimeFilter(key)}
                  >
                    <i className={icon} /> {t(labelKey, key)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Country dropdown (in collapsible section) */}
        {hasWhere && config.where.includes('country') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-group-label">{t('common.country', 'Country')}</div>
              <select className="filter-select filter-select-full" value={search.country}
                onChange={(e) => search.setCountry(e.target.value)}>
                <option value="">{t('common.allCountries', 'All Countries')}</option>
                {countries.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
          </div>
        )}

        {/* Date range */}
        {hasWhen && config.when.includes('dateRange') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-group-label">{t('filter.dateRange', 'Date Range')}</div>
              <div className="filter-date-row">
                <div className="filter-date-field">
                  <label className="filter-date-label">{t('filter.from', 'From')}</label>
                  <input type="date" className="filter-date-input" value={filters.dateFrom}
                    onChange={(e) => filters.setDateFrom(e.target.value)} />
                </div>
                <span className="filter-date-sep">&mdash;</span>
                <div className="filter-date-field">
                  <label className="filter-date-label">{t('filter.to', 'To')}</label>
                  <input type="date" className="filter-date-input" value={filters.dateTo}
                    onChange={(e) => filters.setDateTo(e.target.value)} />
                </div>
                {(filters.dateFrom || filters.dateTo) && (
                  <button className="filter-date-clear"
                    onClick={() => { filters.setDateFrom(''); filters.setDateTo(''); }} title={t('search.clearDates')}>
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Event Sort */}
        {hasWhen && config.when.includes('eventSort') && (
          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-group-label">{t('common.sortEvents', 'Sort')}</div>
              <div className="filter-pills">
                {SORT_OPTIONS.map((so) => (
                  <button key={so.value} className={'filter-pill' + (filters.eventSort === so.value ? ' active' : '')}
                    onClick={() => filters.setEventSort(so.value)}>
                    <i className={so.icon} /> {t(so.labelKey, so.value)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
